import { buildSettingsCatalog, PROFILE_SECTIONS } from '@nexus/core';
import {
  Card,
  CardContent,
  CardHeader,
  DescriptionList,
  MenuList,
  MenuListItem,
  Page,
  PageHeader,
  SettingsSection,
  StatusBadge,
} from '@nexus/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModules } from '../modules/context';
import { moduleSettingsPath, settingsOfModule } from '../modules/settings';
import { useSession } from '../session/session';
import { useSettings } from '../session/settings';
import { LoginSecuritySection } from '../settings/login-security';
import { CHANNEL_SETTING_KEYS, NotificationChannelRows } from '../settings/notification-channels';
import { PersonalSettingRow } from '../settings/setting-rows';
import { SignatureSection } from '../signatures/signature-section';

/**
 * Own account and all personal settings with `placement: 'profile'`, kernel and modules, plus
 * links to the settings pages of modules with personal settings.
 */
export function ProfilePage() {
  const { t } = useTranslation();
  const session = useSession();
  const settings = useSettings();
  const modules = useModules();

  const catalog = useMemo(
    () =>
      buildSettingsCatalog(modules.map((m) => m.manifest)).filter(
        (s) =>
          s.level === 'user' &&
          s.placement === 'profile' &&
          s.key in settings.values &&
          !CHANNEL_SETTING_KEYS.includes(s.key),
      ),
    [modules, settings.values],
  );
  // Personal settings of a module live on its settings page; the profile links there.
  const moduleLinks = modules
    .filter((m) => !m.manifest.parent)
    .filter(
      (m) =>
        settingsOfModule(
          m.manifest,
          modules.map((x) => x.manifest),
          session,
        ).personal.length > 0,
    )
    .map((m) => ({
      id: m.manifest.id,
      icon: m.manifest.icon,
      label: t(m.manifest.name),
      href: moduleSettingsPath(m.manifest.id),
    }));

  return (
    <Page>
      <PageHeader title={t('profile.title')} description={t('profile.description')} />
      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader title={t('profile.account')} />
          <CardContent>
            <DescriptionList
              items={[
                { label: t('profile.name'), value: session.account.displayName },
                { label: t('profile.email'), value: session.account.email ?? '–' },
                { label: t('profile.company'), value: session.company?.name ?? '–' },
                {
                  label: t('profile.signInMethod'),
                  value: (
                    <span className="flex flex-wrap items-center gap-2">
                      {t(`profile.methods.${session.method}`)}
                      <StatusBadge status={session.mfa ? 'success' : 'neutral'}>
                        {session.mfa ? t('profile.mfaYes') : t('profile.mfaNo')}
                      </StatusBadge>
                    </span>
                  ),
                },
              ]}
            />
          </CardContent>
        </Card>

        {(session.method === 'password' || session.method === 'passkey') && (
          <LoginSecuritySection />
        )}
        <SignatureSection />

        {PROFILE_SECTIONS.map((section) => {
          const entries = catalog.filter((s) => s.section === section);
          // Notifications always list their channels, the in-app list at least.
          if (entries.length === 0 && section !== 'notifications') return null;
          return (
            <SettingsSection key={section} title={t(`profile.sections.${section}`)}>
              {section === 'notifications' && <NotificationChannelRows />}
              {entries.map((setting) => (
                <PersonalSettingRow key={setting.key} setting={setting} />
              ))}
            </SettingsSection>
          );
        })}

        {moduleLinks.length > 0 && (
          <Card>
            <CardHeader
              title={t('profile.moduleSettings')}
              description={t('profile.moduleSettingsHint')}
            />
            <MenuList>
              {moduleLinks.map((module) => (
                <MenuListItem key={module.id} icon={module.icon} href={module.href}>
                  {module.label}
                </MenuListItem>
              ))}
            </MenuList>
          </Card>
        )}
      </div>
    </Page>
  );
}
